import { useEffect, useState } from "react";

import { markRuns, type FieldMarks } from "./logFind";
import { FIND_STYLE } from "./Sentence";
import { translationState } from "./translationState";

/**
 * The English under a sentence (日本-102, frame `1a`).
 *
 * **Translation is never automatic.** A sentence the reader understood stays bare, and the ghost
 * `訳 翻訳` is the entire entry point — which is why it appears on the **focused row only**.
 * An unfocused untranslated row shows nothing at all: a button on every line would turn a page of
 * reading into a page of buttons, and the design's whole claim is that the reader asks.
 *
 * Four states, from `docs/AUTO-TRANSLATE.md`:
 *
 *   focused, untranslated    the ghost button + the mono `T` it advertises
 *   in flight                `翻訳中…` and a two-tone bar
 *   focused, translated      17px `--ink`, actions under it
 *   unfocused, translated    15px `--ink-2` (the row is already at 58% — 日本-306)
 *
 * **Aligned to the Japanese, not to the 38px time gutter.** It is a reading of *this sentence* and
 * sits under it; indenting it to the gutter would make it look like a row of its own.
 *
 * Everything here `stopPropagation`s. The row is a focus target and the sentence itself opens the
 * editor, so a click on `remove` that reached either would delete a translation *and* open an edit
 * box over the sentence it belonged to.
 */

export interface TranslationProps {
  translation: string;
  translating: boolean;
  focused: boolean;
  /** False when the deployment has no API key — the affordance is hidden rather than drawn dead. */
  configured: boolean;
  onTranslate: () => void;
  onChange: (text: string) => void;
  onOpenContext: () => void;
  /**
   * Handed down so the open panel can recognise its own trigger.
   *
   * `文脈 ▾` is a toggle sitting *outside* the popover, so without this the popover's
   * outside-click closer fires on `mousedown` and the button's `click` re-opens it from the state
   * that close just wrote — the panel could never be dismissed by the control that opened it.
   */
  contextButtonRef?: React.RefObject<HTMLButtonElement | null>;
  isPhone?: boolean;
  /** The find band's marks in this translation (日本-603). */
  findMarks?: FieldMarks;
}

export function Translation({
  translation,
  translating,
  focused,
  configured,
  onTranslate,
  onChange,
  onOpenContext,
  contextButtonRef,
  isPhone = false,
  findMarks,
}: TranslationProps) {
  const [editing, setEditing] = useState<string | null>(null);

  // A row that loses focus mid-edit would otherwise keep an open field at 58% opacity, on a
  // sentence the reader has moved away from — and the next `edit` elsewhere would find this one
  // still open.
  useEffect(() => {
    if (!focused) setEditing(null);
  }, [focused]);

  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  // Which of the four states this row is in — the rule itself lives in `translationState.ts`,
  // where a test with no DOM can reach it.
  const state = translationState({ translation, translating }, focused, configured);

  if (state === "bare") return null;

  if (state === "translating") {
    return (
      <div className="flex items-center gap-2" onClick={stop}>
        <span className="font-jp text-[15px]/[1] text-ink-4">訳</span>
        <span className="text-[15px]/[1.6] text-ink-4">翻訳中…</span>
        {/* The 22×6 two-tone bar. Not a spinner: it says "a request is out" without claiming to
            know how far along it is, which nothing here does. */}
        <span
          aria-hidden
          className="h-[6px] w-[22px] overflow-hidden rounded-full bg-[color:var(--line-soft)]"
        >
          <span className="block h-full w-1/2 animate-pulse rounded-full bg-[color:var(--accent-line)]" />
        </span>
      </div>
    );
  }

  if (state === "offer") {
    return (
      <button
        type="button"
        onClick={(event) => {
          stop(event);
          onTranslate();
        }}
        title="この文を翻訳"
        className="flex h-6 w-fit items-center gap-[6px] rounded-[var(--r-2)] border border-dashed border-[color:var(--line-strong)] px-[9px] text-ink-3 hover:border-[color:var(--accent-line)] hover:text-[color:var(--accent-text)]"
      >
        <span className="font-jp text-[14px]/[1]">訳</span>
        <span className="text-[14px]/[1]">翻訳</span>
        {!isPhone && (
          <span className="font-mono text-[14px]/[1] text-ink-4">T</span>
        )}
      </button>
    );
  }

  if (editing !== null) {
    return (
      // **`draggable={false}` and the stopped `dragstart`.** The whole row is draggable — that is
      // how a sentence is moved to another log — so dragging to select text in here would pick the
      // sentence up instead of selecting anything.
      <div onClick={stop} draggable={false} onDragStart={stop}>
        <textarea
          autoFocus
          rows={2}
          value={editing}
          onChange={(event) => setEditing(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          onBlur={() => {
            onChange(editing.trim());
            setEditing(null);
          }}
          onKeyDown={(event) => {
            // Stopped as well as prevented: Read's window handler owns every bare letter on this
            // screen, and `a`, `e`, `j`, `k` and `T` are all things you type in an English
            // sentence. `isTextEntry` covers this too, and both are cheap.
            event.stopPropagation();
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onChange(editing.trim());
              setEditing(null);
            } else if (event.key === "Escape") {
              event.preventDefault();
              setEditing(null);
            }
          }}
          className="w-full resize-none rounded-[var(--r-2)] border-[1.5px] border-accent bg-panel px-[9px] py-[5px] text-[17px]/[1.5] text-ink outline-none"
        />
      </div>
    );
  }

  return (
    // **`draggable={false}` and the stopped `dragstart`, exactly as the edit box above.** The whole
    // row is draggable — that is how a sentence is moved to another log — so dragging across the
    // English to select it for copying made the row the drag source, and releasing over a tab in
    // the strip moved the sentence into that log.
    <div
      className="flex flex-col gap-[3px]"
      onClick={stop}
      draggable={false}
      onDragStart={stop}
    >
      <p className="flex gap-2">
        <span
          aria-hidden
          className="shrink-0 font-jp text-[15px]/[1.6]"
          style={{ color: focused ? "var(--accent-line)" : "var(--ink-4)" }}
        >
          訳
        </span>
        <span
          style={{
            fontSize: focused ? 17 : 15,
            lineHeight: 1.6,
            color: focused ? "var(--ink)" : "var(--ink-2)",
          }}
        >
          {markRuns(translation, findMarks).map((run, i) =>
            run.mark ? (
              <span key={i} data-find={run.mark} style={FIND_STYLE[run.mark]}>
                {run.text}
              </span>
            ) : (
              run.text
            ),
          )}
        </span>
      </p>

      {/* Actions on the focused row only. Elsewhere the English is something you read past. */}
      {focused && (
        <div className="flex items-center gap-[7px] pl-[21px] text-[14px]/[1.5]">
          <Action onClick={() => setEditing(translation)}>編集</Action>
          <Dot />
          {/* Gated on `configured` for the ghost button's reason and to the same end: on a
              deployment with no key this would be an affordance that always 503s. What is already
              written stays on screen — losing the key is not losing the log — but nothing offers to
              produce a new one. */}
          {configured && (
            <>
              <Action onClick={onTranslate}>再翻訳</Action>
              <Dot />
            </>
          )}
          {/* **No confirm.** The row goes back to bare and `翻訳` is one click away — the
              design is explicit that this is not a destructive act worth a dialog. */}
          <Action onClick={() => onChange("")}>削除</Action>
          <span aria-hidden className="mx-[3px] h-[11px] w-px bg-[color:var(--line)]" />
          <button
            type="button"
            ref={contextButtonRef}
            onClick={onOpenContext}
            className="text-[color:var(--accent-text)] hover:underline"
          >
            文脈 ▾
          </button>
        </div>
      )}
    </div>
  );
}

function Action({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="text-ink-3 hover:text-ink-2 hover:underline">
      {children}
    </button>
  );
}

function Dot() {
  return (
    <span aria-hidden className="text-ink-4">
      ·
    </span>
  );
}
